import { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import NiceModal from "@ebay/nice-modal-react";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import IconButton from "/src/components/UI/Button/IconButton/IconButton";
import RoleModal from "../RoleModal/RoleModal";
import roleAdminService from "../../services/roleAdminService";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import "./RoleTable.css";

function RoleTable() {
    const toast = useToastDispatchContext();
    const { getRoles, getPermissions, deleteRole } = roleAdminService;
    const [rows, setRows] = useState([]);
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const [[rolesResponse, rolesError], [permissionsResponse, permissionsError]] = await Promise.all([
                getRoles(),
                getPermissions(),
            ]);

            if (rolesError || permissionsError) {
                toast.error("Error", "Unable to load roles");
                return;
            }

            setRows(rolesResponse || []);
            setPermissions(permissionsResponse || []);
        };
        fetchData();
    }, []);

    const permissionLabelById = Object.fromEntries(permissions.map((p) => [p.id, p.label]));

    const onClickAdd = () =>
        NiceModal.show(RoleModal, { title: "Create a new role", permissions })
            .then((role) => setRows((prev) => [role, ...prev]));

    const onClickUpdate = (row) =>
        NiceModal.show(RoleModal, { title: "Edit role", data: row, permissions })
            .then((role) => setRows((prev) => prev.map((r) => (r.id === role.id ? role : r))));

    const onClickDelete = (row) =>
        ModalManager.show(ApiModal, {
            title: "Delete role " + row.name,
            body: "Do you really want to delete the role \"" + row.name + "\" ? Users assigned to it will lose the permissions it granted.",
            api: { request: deleteRole, param: [row.id] },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Delete", type: "submit" },
            ],
        }).then((res) => {
            if (res?.error != null) {
                toast.error("Error", res.error);
                return;
            }
            setRows((prev) => prev.filter((r) => r.id !== row.id));
        });

    const columnDefs = [
        { headerName: "Name", field: "name", width: 200, sortable: true, filter: true, floatingFilter: true },
        { headerName: "Description", field: "description", flex: 1, sortable: true, filter: true },
        {
            headerName: "Permissions",
            field: "permissions",
            flex: 1.5,
            cellRenderer: (params) => (
                <span>
                    {(params.value || [])
                        .map((grant) => {
                            const label = permissionLabelById[grant.permission_id];
                            if (!label) return null;
                            const unitCount = grant.unit_ids?.length || 0;
                            return unitCount > 0 ? `${label} (${unitCount} unit${unitCount > 1 ? "s" : ""})` : label;
                        })
                        .filter(Boolean)
                        .join(", ") || "-"}
                </span>
            ),
        },
        {
            headerName: "Actions",
            width: 100,
            pinned: "right",
            cellRenderer: (params) => (
                <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                    {params.data?.id !== 0 && <i className="fas fa-edit action-button blue" onClick={() => onClickUpdate(params.data)} />}
                    {params.data?.id !== 0 && <i className="fas fa-trash action-button red" onClick={() => onClickDelete(params.data)} />}
                </div>
            ),
        },
    ];

    return (
        <div className="role-admin-grid-section">
            <div className="role-admin-toolbar">
                <IconButton
                    className="main-iconButton profil_action role-admin-add-button"
                    icon="fas fa-plus"
                    onClick={onClickAdd}
                    title="Add role"
                />
            </div>
            <div className="ag-theme-alpine role-admin-grid" style={{ width: "100%", height: "550px" }}>
                <AgGridReact
                    enableCellTextSelection={true}
                    rowData={rows}
                    rowHeight={56}
                    columnDefs={columnDefs}
                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                    pagination={true}
                    paginationPageSize={50}
                />
            </div>
        </div>
    );
}

export default RoleTable;
